interface FormattedVerse {
  number: string;
  text: string;
}

// Format the Bible text by adding proper spacing, removing unnecessary symbols, and trimming excess whitespace
export const formatBibleText = (text: string): string => {
  if (!text) return '';
  
  return text
    .replace(/(\d+)([^.])/g, '$1. $2')   // Ensure a space after verse numbers before text
    .replace(/\s+/g, ' ')                 // Collapse multiple spaces into a single space
    .replace(/\.(\S)/g, '. $1')           // Ensure a space after periods if followed by a letter
    .replace(/\*\*/g, '')                 // Remove any double asterisks
    .replace(/\[|\]/g, '')                // Remove square brackets
    .replace(/(\d)([A-Za-z])/g, '$1. $2') // Insert period and space between chapter numbers and text
    .trim();                              // Trim extra spaces from the start and end
};

// Parse verses from text and split them into verse numbers and corresponding text
export const parseVerses = (text: string): FormattedVerse[] => {
  const verses: FormattedVerse[] = [];
  const lines = text.split('\n').map(line => line.trim().replace(/\s+/g, ' '));  // Ensure each line has collapsed spaces

  for (const line of lines) {
    if (!line) continue;

    // Check if the line is a new section header (no verse number)
    const isNewSection = line.match(/^\d+\./) == null;

    if (isNewSection) {
      // Add the entire line as a new section without verse numbers
      verses.push({ number: '', text: formatBibleText(line) });
    } else {
      // Match the verse number and text, ensuring proper formatting
      const verseMatch = line.match(/^(\d+)\.\s*(.*)/);
      if (verseMatch) {
        verses.push({
          number: verseMatch[1], 
          text: formatBibleText(verseMatch[2])  // Apply formatting to verse text
        });
      }
    }
  }

  return verses;
};

// Utility to split Bible references, normalizing spaces and removing any extra words
export const splitBibleReferences = (reference: string): string[] => {
  return reference
    .split(/\s*(and|or)\s*/i)   // Split the string based on "and" or "or", case insensitive
    .map(ref => ref.trim())      // Trim extra spaces from each part
    .filter(ref => ref);         // Remove empty parts
};
